import { Controller } from "@hotwired/stimulus"
import { startIcons } from "../import_lucid"
import { openSheet, openDocument } from "../sheet_viewer"
import * as echarts from 'echarts'

// Loads pipeline result HTML inline within the message bubble
export default class extends Controller {
  static values = {
    url: String,
    sql: String,
    meta: String,
    followUps: Array,
    fresh: Boolean // just broadcast, not reloaded with the chat's history
  }

  connect() {
    if (this.urlValue) {
      this.loadResult()
    }
  }

  disconnect() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
    }
  }

  loadResult() {
    const container = this.element
    const resultContent = container.querySelector('.result-content')
    const sqlSection = container.querySelector('.sql-section')
    const sqlContent = sqlSection?.querySelector('[data-sql-toggle-target="content"]')
    const followUpsSection = container.querySelector('.follow-ups-section')
    const followUpButtons = followUpsSection?.querySelector('.laz-suggest')

    if (!resultContent) return

    fetch(this.urlValue)
      .then(response => response.text())
      .then(html => {
        resultContent.innerHTML = html
        startIcons(resultContent)

        // Wire up copy & download buttons
        this._initCardButtons(resultContent)

        // Resize AI charts dynamically on window resize
        this._initChartResize(resultContent)

        // Show SQL section if SQL is available
        if (sqlSection && this.sqlValue) {
          sqlSection.classList.remove('hidden')
          if (sqlContent) sqlContent.textContent = this.sqlValue
        }

        // Show follow-up suggestions
        if (followUpsSection && followUpButtons && this.followUpsValue && this.followUpsValue.length > 0) {
          followUpButtons.innerHTML = ''
          // Rotate through chip icons
          const chipIcons = [
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-6 4 3 5-7"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18M12 3l9 9-9 9"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h18M6 12h12M10 17h4"/></svg>',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg>'
          ]
          this.followUpsValue.forEach((text, i) => {
            const btn = document.createElement('button')
            btn.className = 'laz-chip'
            btn.innerHTML = chipIcons[i % chipIcons.length] + ' ' + this._escapeHtml(text)
            btn.addEventListener('click', () => {
              const input = document.getElementById('chat-input')
              if (input) {
                input.value = text
                input.focus()
              }
            })
            followUpButtons.appendChild(btn)
          })
          followUpsSection.classList.remove('hidden')
        }

        // Hide suggestions area after first result
        const suggestionsArea = document.getElementById('suggestions-area')
        if (suggestionsArea) suggestionsArea.remove()
      })
      .catch(err => {
        resultContent.innerHTML = '<div class="p-3 text-ui text-state-bad">Failed to load results</div>'
        console.error('Pipeline result load error:', err)
      })
  }

  _initCardButtons(container) {
    this._initSpreadsheetCards(container)

    // Copy button — copies chart as PNG image, or table data as tab-separated text
    container.querySelectorAll('.laz-copy-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        // A prose table carries a copy button too. It is not a .laz-card — it has none of the
        // card's machinery — so the lookup has to know about both containers or the button on a
        // markdown table silently does nothing.
        const card = btn.closest('.laz-card, .laz-mdtbl-wrap')
        if (!card) return

        // Glance chart card — the chart is one SVG; paint it onto a canvas for a PNG. Its
        // colours and fonts are var(--token, fallback), and an <img> sees no page tokens, so the
        // image comes out in the light palette on white, the same as the ECharts copy did.
        const glanceSvg = card.querySelector('[data-controller="glance"] svg')
        if (glanceSvg) {
          navigator.clipboard.write([new ClipboardItem({ 'image/png': this._svgToPng(glanceSvg) })]).catch(() => {})
          this._showCheckmark(btn, 'Copied!', 'Copy chart')
          return
        }

        // Chart card from before the Glance switch — copy as PNG image
        const chartEl = card.querySelector('[data-controller="apache-echarts"]')
        if (chartEl) {
          const instance = echarts.getInstanceByDom(chartEl)
          if (instance) {
            const dataUrl = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
            fetch(dataUrl)
              .then(res => res.blob())
              .then(blob => navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]))
              .catch(() => {})
            this._showCheckmark(btn, 'Copied!', 'Copy chart')
            return
          }
        }

        // Table or stat — copy as text
        const tbl = card.querySelector('table')
        let text = ''
        if (tbl) {
          const rows = []
          tbl.querySelectorAll('tr').forEach(r => {
            const cells = []
            r.querySelectorAll('th, td').forEach(c => cells.push(c.textContent.trim()))
            rows.push(cells.join('\t'))
          })
          text = rows.join('\n')
        } else {
          const val = card.querySelector('.laz-stat-value')
          text = val ? val.textContent.trim() : ''
        }
        navigator.clipboard.writeText(text).catch(() => {})
        this._showCheckmark(btn, 'Copied!', 'Copy data')
      })
    })

    // Download button — server-side CSV export of the FULL result (not just 50-row preview).
    // Click flow:
    //   1. Spin + disable immediately so the user knows the click registered
    //   2. POST /export_csv — backend enqueues FileDownloadJob, returns 200 with download id
    //   3. Keep the spinner running; the final "Download ready" toast arrives via Turbo
    //      broadcast (see turbo_stream_from in _pipeline_message.html.erb) to #flash_message
    //   4. On error, restore button to error state briefly then back to normal
    container.querySelectorAll('.laz-download-btn').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (!this.urlValue) {
          console.warn('[chat-download] no urlValue on pipeline-result controller')
          return
        }
        if (btn.dataset.downloading === '1') return  // double-click guard
        const urlObj = new URL(this.urlValue, window.location.origin)
        const url = urlObj.pathname + '/export_csv' + urlObj.search
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        this._startDownloadSpinner(btn)
        console.log('[chat-download] POST', url)
        fetch(url, {
          method:  'POST',
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': token || '' }
        })
          .then(r => r.ok ? r.json() : Promise.reject(r))
          .then(json => {
            console.log('[chat-download] queued', json)
            // Keep spinning — the actual completion toast arrives via Turbo broadcast.
            // Safety: clear after 60s in case the broadcast is missed.
            setTimeout(() => this._stopDownloadSpinner(btn), 60000)
          })
          .catch(err => {
            console.warn('[chat-download] failed', err)
            this._stopDownloadSpinner(btn)
            this._showCheckmark(btn, 'Failed', 'Download full result as CSV', 2500)
          })
      })
    })
  }

  // The Excel card (Chat::Spreadsheet) and the Word / PowerPoint one (Chat::Document). Its file hangs
  // off THIS message, which did not exist when the card was drawn, so its links are built here from
  // the message url.
  _initSpreadsheetCards(container) {
    if (!this.urlValue) return
    const urlObj = new URL(this.urlValue, window.location.origin)
    const at = (path, param) => {
      const url = new URL(urlObj.pathname + path + urlObj.search, window.location.origin)
      if (param) url.searchParams.set(...param)
      return url.pathname + url.search
    }
    container.querySelectorAll('.laz-xfile').forEach(card => {
      // A Word document or deck (Chat::Document) has a second link, its Markdown
      const doc = card.hasAttribute('data-doc')
      const downloadUrl = at(doc ? '/document' : '/spreadsheet')
      const mdUrl = at('/document', ['as', 'md'])
      card.querySelectorAll('a.laz-xfile-dl:not([data-doc-app])').forEach(download => {
        download.href = download.hasAttribute('data-doc-md') ? mdUrl : downloadUrl
        download.dataset.turbo = 'false' // a Turbo visit to a file never finishes: its bar sat at the top
      })
      const open = card.querySelector('[data-sheet-open], [data-doc-open]')
      open.addEventListener('click', () => doc
        ? openDocument({ title: open.dataset.docOpen, jsonUrl: at('/document.json'), downloadUrl, mdUrl })
        : openSheet({ title: open.dataset.sheetOpen, jsonUrl: at('/spreadsheet.json'), downloadUrl }))
    })
    // An answer that has just arrived opens its file beside the chat, as claude.ai opens a new
    // artifact; one reloaded with the chat's history waits for a click. Not on a phone, where the
    // panel would cover the answer it belongs to.
    const fresh = container.querySelector('.laz-xfile [data-sheet-open], .laz-xfile [data-doc-open]')
    if (fresh && this.freshValue && window.matchMedia('(min-width: 768px)').matches) fresh.click()
    this.freshValue = false // once: the page Turbo keeps for Back must not open it again
  }

  _showCheckmark(btn, tempTitle, restoreTitle, duration = 1500) {
    const orig = btn.innerHTML
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px;color:#10b981"><path d="M20 6 9 17l-5-5"/></svg>'
    btn.title = tempTitle
    setTimeout(() => { btn.innerHTML = orig; btn.title = restoreTitle }, duration)
  }

  // Show a rotating spinner inside the download button while the export job runs.
  // The download is async — the button stays spinning until the Turbo broadcast
  // completion toast arrives (or the 60s safety timeout in the click handler fires).
  _startDownloadSpinner(btn) {
    if (btn.dataset.downloading === '1') return
    btn.dataset.downloading = '1'
    btn.dataset.originalHtml = btn.innerHTML
    btn.dataset.originalTitle = btn.title || ''
    btn.disabled = true
    btn.title = 'Preparing your download…'
    btn.style.cursor = 'wait'
    btn.innerHTML = '<svg class="laz-download-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" style="width:13px;height:13px;color:currentColor;animation: laz-spin 0.8s linear infinite"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>'
    // Inject keyframes once
    if (!document.getElementById('laz-download-spinner-kf')) {
      const style = document.createElement('style')
      style.id = 'laz-download-spinner-kf'
      style.textContent = '@keyframes laz-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'
      document.head.appendChild(style)
    }
    // Watch #flash_message for the "Download Ready" toast; stop the spinner when it lands.
    const flash = document.getElementById('flash_message')
    if (flash) {
      const observer = new MutationObserver(() => {
        if (flash.textContent.includes('Download Ready') || flash.querySelector('a[href*="csv"]')) {
          this._stopDownloadSpinner(btn)
          this._showCheckmark(btn, 'Download ready!', 'Download full result as CSV', 2000)
          observer.disconnect()
        }
      })
      observer.observe(flash, { childList: true, subtree: true, characterData: true })
      btn._downloadObserver = observer
    }
  }

  _stopDownloadSpinner(btn) {
    if (btn.dataset.downloading !== '1') return
    if (btn._downloadObserver) {
      btn._downloadObserver.disconnect()
      delete btn._downloadObserver
    }
    btn.disabled = false
    btn.style.cursor = ''
    btn.title = btn.dataset.originalTitle || 'Download full result as CSV'
    btn.innerHTML = btn.dataset.originalHtml || btn.innerHTML
    delete btn.dataset.downloading
    delete btn.dataset.originalHtml
    delete btn.dataset.originalTitle
  }

  // A Promise<Blob>, not a Blob: ClipboardItem takes the promise, which keeps the write inside
  // the click (Safari refuses a clipboard write that starts after an await).
  _svgToPng(svg) {
    const box = svg.viewBox.baseVal, scale = 2
    const img = new Image()
    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = box.width * scale
        canvas.height = box.height * scale
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#fff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob(resolve, 'image/png')
      }
      img.onerror = reject
      img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg))
    })
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Resize only AI chat charts on window resize
  _initChartResize(container) {
    const chartEls = container.querySelectorAll('[data-controller="apache-echarts"]')
    if (chartEls.length === 0) return

    this._resizeHandler = () => {
      chartEls.forEach(el => {
        const instance = echarts.getInstanceByDom(el)
        if (instance) instance.resize()
      })
    }
    window.addEventListener('resize', this._resizeHandler)
  }
}
